import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { StyleSheet, Pressable, Animated } from 'react-native';
import { AnimatedDNABox, DNABox, DNAConditionalWrapper, DNAIcon, DNAText, Iffy, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, DNAPopover } from 'src/components/DNA';
import useMachineSelector, { composite } from 'src/hooks/useSelector';
import * as navigationMenuSelector from 'src/state/machines/navigationMenu/navigationMenu.selector';
import {
  useNavigationMenuState,
} from 'src/state/machines/navigationMenu/NavigationMenuProvider';
import { useAppSettings } from 'src/state/context/AppSettings';
import { NavigationItemConfig } from 'src/state/machines/navigationMenu/navigationMenu.types';

const S = StyleSheet.create({
  navigationRootItemContainer: {
    paddingHorizontal: 16,
  },
  navigationChildrenItemContainer: {
    paddingLeft: 13,
  },
  navigationChildrenItemsContainer: {
    marginLeft: 25,
    borderLeftWidth: 2,
    borderLeftColor: colors['color-white-transparent-16'],
  },
  navigationItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 6,
    height: 40,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginVertical: 2,
  },
  navigationItemTablet: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    height: 56,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginVertical: 2,
  },
  hoveredNavigationItem: {
    backgroundColor: colors['color-white-transparent-16'],
  },
  navItemIcon: {
    width: 22,
    height: 22,
  },
  navItemIconTablet: {
    width: 28,
    height: 28,
  },
  navItemText: {
    fontWeight: '600',
    flexShrink: 1,
  },
  expandIndicator: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  tooltipRoot: {
    position: 'absolute',
    left: '100%',
    zIndex: 1000,
  },
  tooltipContainer: {
    backgroundColor: colors['color-tooltip-background'],
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  contextMenuItemsContainer: {
    backgroundColor: colors['color-ui2-brand-900'],
    padding: 12,
    borderRadius: 10,
  },
})

const DISABLED_POPOVER_PLATFORMS = ['tabletPWA', 'tabletWEB'];

type TooltipProps = {
  text: string
  visible: boolean
}

type NavigationItemProps = {
  navigationItem: NavigationItemConfig
  depthIndex: number
  callback?: () => void
}

const Tooltip: React.FC<TooltipProps> = (props) => {
  const { text, visible } = props;
  if (!visible) return null;
  return (
    <DNABox style={S.tooltipRoot}>
      <DNABox style={S.tooltipContainer}>
        <DNAText colorMode="dark">{text}</DNAText>
      </DNABox>
    </DNABox>
  )
}

const NavigationItem: React.FC<NavigationItemProps> = (props) => {
  const { navigationItem, depthIndex, callback } = props;
  const { deviceMode, platform } = useAppSettings();
  const { service, navigateTo } = useNavigationMenuState();
  const [contextMenuVisible, setContextMenuVisible] = useState<boolean>(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const isTablet = deviceMode === 'tablet';

  const cond = useMachineSelector(
    service,
    (state) => composite(
      state,
      navigationMenuSelector.isMenuCollapsed,
      navigationMenuSelector.currentRoute,
      navigationMenuSelector.shouldExpandChildren,
    ),
  )

  const isRootRoute = depthIndex === 0;
  const disablePopover = DISABLED_POPOVER_PLATFORMS.includes(platform) || !isRootRoute;
  const isToolTipVisible = useMemo(
    () => {
      return !disablePopover && cond.isMenuCollapsed && showTooltip && !contextMenuVisible && isRootRoute;
    },
    [disablePopover, cond.isMenuCollapsed, showTooltip, contextMenuVisible, isRootRoute],
  );
  const [childrenExpanded, setChildrenExpanded] = useState<boolean | undefined>();

  useEffect(() => {
    setChildrenExpanded(undefined);
  }, [cond.currentRoute?.PATH]);

  const shouldExpandChildrenInExpandMode = useMemo(
    () => !cond.isMenuCollapsed && (childrenExpanded ?? cond.shouldExpandChildren(navigationItem)),
    [childrenExpanded, cond.isMenuCollapsed, cond.shouldExpandChildren, navigationItem],
  );

  // Animation setup for vertical expand/collapse
  const animatedHeight = useRef(new Animated.Value(0)).current;
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const animation = Animated.timing(animatedHeight, {
      toValue: shouldExpandChildrenInExpandMode ? contentHeight : 0,
      duration: 250,
      useNativeDriver: false,
    });
    animation.start();
    return () => {
      animation.stop();
    };
  }, [shouldExpandChildrenInExpandMode, contentHeight, animatedHeight]);

  const hasChildrenPath = !!navigationItem?.children?.length;
  const isCurrentPage = cond.currentRoute?.PATH === navigationItem.route.PATH;

  // Check if any child route is currently active
  const hasActiveChild = useMemo(() => {
    if (!hasChildrenPath) return false;
    return navigationItem.children?.some(child =>
      cond.currentRoute?.PATH === child.route.PATH,
    );
  }, [hasChildrenPath, navigationItem.children, cond.currentRoute?.PATH]);

  const iconName = useMemo(() => `${navigationItem.route.ICON}${(isCurrentPage || hasActiveChild) ? '-fill' : ''}`,
    [isCurrentPage, hasActiveChild, navigationItem.route.ICON]);

  const pressableStyle = useCallback(({ hovered }) => {
    return [
      !isTablet && S.navigationItem,
      isTablet && S.navigationItemTablet,
      ((isCurrentPage && !hasChildrenPath) || hovered) && S.hoveredNavigationItem,
    ]
  }, [isCurrentPage, isTablet]);

  // Compose animated style
  const childrenItemsContainerStyleExpandMode = useMemo(() => util.mergeStyles(
    undefined,
    S.navigationChildrenItemsContainer,
    { overflow: 'hidden', height: animatedHeight },
  ), [animatedHeight]);

  const toggleChildrenContextMenu = useCallback(() => setContextMenuVisible(p => !p), [setContextMenuVisible]);
  const closeContextMenu = useCallback(() => setContextMenuVisible(false), [setContextMenuVisible]);

  const onPressItem = useCallback(() => {
    if (isRootRoute) {
      if (!cond.isMenuCollapsed && hasChildrenPath) {
        setChildrenExpanded(!shouldExpandChildrenInExpandMode);
        return;
      }
      if (!cond.isMenuCollapsed || !hasChildrenPath) {
        navigateTo(navigationItem.route)
      }
      else if (cond.isMenuCollapsed && hasChildrenPath) {
        if (!hasActiveChild) {
          navigateTo(navigationItem.route)
        }
        toggleChildrenContextMenu()
      }
    }

    if (!isRootRoute) {
      navigateTo(navigationItem.route)
      setContextMenuVisible(false)
    }

    callback?.();
  }, [
    navigateTo,
    navigationItem.route,
    isRootRoute,
    hasChildrenPath,
    cond.isMenuCollapsed,
    shouldExpandChildrenInExpandMode,
    toggleChildrenContextMenu,
    contextMenuVisible,
    hasActiveChild,
    callback,
  ])

  // Currently the UI design only supports 1 level of nested routes, but the machine supports unlimited levels
  if (depthIndex > 1) return null;

  return (
    <DNABox appearance="col" style={isRootRoute ? S.navigationRootItemContainer : S.navigationChildrenItemContainer}>
      <DNAConditionalWrapper
        condition={cond.isMenuCollapsed && hasChildrenPath}
        wrapper={children => (
          <DNAPopover
            lazyMount
            interactive={true}
            visible={contextMenuVisible}
            placement="right"
            onBackdropPress={toggleChildrenContextMenu}
            type="menu"
          >
            <DNAPopover.Anchor>
              {children}
            </DNAPopover.Anchor>
            <DNAPopover.Content offset={24}>
              <DNABox style={S.contextMenuItemsContainer}>
                <DNABox appearance="col" style={S.navigationChildrenItemsContainer}>
                  {navigationItem.children?.map((childNavigationItem) => (
                    <NavigationItem
                      key={childNavigationItem.route.PATH}
                      navigationItem={childNavigationItem}
                      depthIndex={depthIndex + 1}
                      callback={closeContextMenu}
                    />
                  ))}
                </DNABox>
              </DNABox>
            </DNAPopover.Content>
          </DNAPopover>
        )}
      >
        <Pressable
          // @ts-ignore: hovered does not exist in PressableStateCallbackType but works
          style={pressableStyle}
          onPress={onPressItem}
          accessibilityRole="button"
          accessibilityState={hasChildrenPath && !cond.isMenuCollapsed
            ? { expanded: shouldExpandChildrenInExpandMode }
            : undefined}
          onHoverIn={() => !disablePopover && setShowTooltip(true)}
          onHoverOut={() => !disablePopover && setShowTooltip(false)}
        >
          <Tooltip text={navigationItem.route.LABEL} visible={isToolTipVisible} />
          <Iffy is={isRootRoute}>
            <DNAIcon.Styled
              appearance="filled"
              name={iconName}
              style={isTablet ? S.navItemIconTablet : S.navItemIcon}
            />
          </Iffy>
          <Iffy is={!cond.isMenuCollapsed || (cond.isMenuCollapsed && !isRootRoute)}>
            <DNAText colorMode="dark" b2={!isTablet} h4={isTablet} style={S.navItemText} numberOfLines={1}>
              {navigationItem.route.LABEL}
            </DNAText>
            <Iffy is={navigationItem.count}>
              <DNAChip.Beacon appearance="filled" status="danger" children={`${navigationItem.count}`} />
            </Iffy>
            <Iffy is={hasChildrenPath && !cond.isMenuCollapsed}>
              <DNABox alignY="center" style={S.expandIndicator}>
                <DNAIcon.Styled
                  appearance="filled"
                  name={shouldExpandChildrenInExpandMode ? 'caret-up' : 'caret-down'}
                  width={isTablet ? 20 : 16}
                  height={isTablet ? 20 : 16}
                />
              </DNABox>
            </Iffy>
          </Iffy>
        </Pressable>
      </DNAConditionalWrapper>
      {/* Animated expand/collapse for children */}
      <AnimatedDNABox appearance="col" style={childrenItemsContainerStyleExpandMode}>
        <DNABox appearance="col" onLayout={e => setContentHeight(e.nativeEvent.layout.height)}>
          {navigationItem.children?.map((childNavigationItem) => (
            <NavigationItem
              key={childNavigationItem.route.PATH}
              navigationItem={childNavigationItem}
              depthIndex={depthIndex + 1}
            />
          ))}
        </DNABox>
      </AnimatedDNABox>
    </DNABox>
  );
}

export default NavigationItem;
